import React, { useEffect, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { AddNewMeetingRoomBooking, CancelMeetingRoomBooking, GetMeetingRoomBookings, GetMeetingRooms } from '../../services/backend';
import moment from 'moment';
import './style.css';
import { BottomUXSpace, ModalX } from '../../components';
import { Button, Option, Select, Textarea, Input, Spinner, Alert, IconButton, Tooltip, Chip, Progress, TabPanel, TabsHeader, Tab, Tabs, TabsBody } from '@material-tailwind/react';
import { minutesToText, Notify, NotifyError } from '../../utils/functions';
import { CalendarIcon, InfoIcon, ListIcon, TrashIcon } from 'lucide-react';
import { useAuth } from '../../services/contexts/authContext';
import { VERIFICATION_TYPES } from '../../utils/enums';
import { useConfirm } from '../../custom-hooks/confirm-dialog';
const localizer = momentLocalizer(moment)

const INITIAL_FORM_DATA_FOR_BOOKING = {
    user: '',
    meetingRoom: '',
    title: '',
    description: '',
    startDate: '',
    slot: '',
}

const CustomerMeetingRoom = () => {
    const [events, setEvents] = useState([]);
    const [meetingRoomBookings, setMeetingRoomBookings] = useState([]);
    // const [selectedEvent, setSelectedEvent] = useState(null);
    const [bookingFormData, setBookingFormData] = useState(INITIAL_FORM_DATA_FOR_BOOKING);
    const [modalOpenForBooking, setModalOpenForBooking] = useState(false);
    const [loading, setLoading] = useState(true);
    const [meetingRooms, setMeetingRooms] = useState([]);
    const [refreshRequired, setRefreshRequired] = useState(0);
    const [view, setView] = useState('month');
    const [loadingMutation, setLoadingMutation] = useState(false);

    const { userDetails } = useAuth();
    const { confirm, ConfirmationDialog } = useConfirm();

    useEffect(() => {
        const fetchMeetingRooms = async () => {
            try {
                setLoading(true);
                const response = await GetMeetingRooms();
                setMeetingRooms(response.data.meetingRooms);
            } catch (error) {
                console.error(error);
                NotifyError(error.response.data.message, error);
            }
            finally {
                setLoading(false);
            }
        }
        const fetchEvents = async () => {
            try {
                const response = await GetMeetingRoomBookings();
                setMeetingRoomBookings(response.data.meetingRoomBookings);
                const events = response.data.meetingRoomBookings.filter((booking) => booking.status !== VERIFICATION_TYPES.CANCELLED).map((booking) => ({
                    title: booking.title,
                    start: new Date(booking.startDate),
                    end: new Date(booking.endDate),
                    resourceId: booking.meetingRoom._id,
                    color: booking.meetingRoom.idColor,
                }));
                setEvents(events);
            } catch (error) {
                console.error(error);
                NotifyError(error.response.data.message, error);
            }
        }
        fetchMeetingRooms();
        fetchEvents();
    }, [refreshRequired]);

    const handleOnTimeSlotSelect = (slotInfo) => {
        if (view === 'day' || view === 'week') {
            setBookingFormData({
                ...bookingFormData,
                startDate: slotInfo.start,
                meetingRoom: slotInfo.resourceId,
            });
            setModalOpenForBooking(true);
        }
        else if (view === 'month') {
            // setView('day');
        }
    };
    // Handle form input change
    // const handleInputChange = async (e) => {
    //     const { name, value } = e.target;
    //     setFormData((prev) => ({
    //         ...prev,
    //         [name]: value,
    //     }));
    // };

    // const handleSelectChange = (name, value) => {
    //     setFormData((prev) => ({
    //         ...prev,
    //         [name]: value,
    //     }));
    // };

    const handleBookingFormSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoadingMutation(true);
            // Add new building logic
            const newMeetingRoomBooking = {
                user: bookingFormData.user,
                meetingRoom: bookingFormData.meetingRoom,
                title: `${userDetails.name} - ${bookingFormData.title}`,
                description: `${userDetails.name} - ${userDetails.email} - ${bookingFormData.description || 'No Description'}`,
                startDate: bookingFormData.startDate,
                slot: bookingFormData.slot,
            };
            await AddNewMeetingRoomBooking(newMeetingRoomBooking);
            setRefreshRequired(refreshRequired + 1);
            Notify('Meeting Room Booked Successfully', 'success');
            setModalOpenForBooking(false);
            setBookingFormData(INITIAL_FORM_DATA_FOR_BOOKING);
        }
        catch (error) {
            console.error(error);
            NotifyError(error.response.data.message, error);
        }
        finally {
            setLoadingMutation(false);
        }
    }

    const handleCancelBooking = async (bookingId) => {
        const confirmation = await confirm({
            title: 'Are you sure you want to cancel this booking?',
            message: 'This action cannot be undone.',
            confirmText: 'Yes, Cancel',
            cancelText: 'No, Keep it',
            confirmBtnColor: 'red',
        });

        if (confirmation) {
            try {
                await CancelMeetingRoomBooking(bookingId);
                setRefreshRequired(refreshRequired + 1);
                Notify('Meeting Room Booking Cancelled Successfully', 'success');
            }
            catch (error) {
                console.error(error);
                NotifyError(error.response.data.message, error);
            }
        }
    }

    if (loading) {
        return <div className="flex justify-center items-center h-screen">
            <Spinner />
        </div>
    }

    const remainingMeetingRoomQuota = 100 - (parseInt((userDetails.meetingRoomQuota.usedHours / userDetails.meetingRoomQuota.monthlyHours) * 100))

    return (
        <div className="p-4 space-y-6">
            <div className="flex justify-between">
                <div className="mb-6 gap-4">
                    <div className="text-2xl font-thin">
                        Meeting Room Bookings
                    </div>
                    <div className="text-sm text-gray-500">
                        Click on the Date to book a Meeting Room.
                    </div>
                </div>
                <div>
                    <Tooltip placement='bottom-start' content={
                        <div>
                            <div className="text-lg font-bold">
                                How to book a Meeting Room?
                            </div>
                            <div>
                                1. Click on the Date in the Calendar.
                            </div>
                            <div>
                                2. Then click on the Time Slot you want to book.
                            </div>
                            <div>
                                3. Select the Meeting Room you want to book.
                            </div>
                            <div>
                                4. Enter the Title, Description and Slot.
                            </div>
                            <div>
                                5. Click on the Book button.
                            </div>
                        </div>
                    }>
                        <IconButton color='blue' className="rounded-full">
                            <InfoIcon />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>

            <Tabs value={'calendar'}>
                <TabsHeader className='bg-gray-200 border border-gray-200 shadow-none rounded-2xl'>
                    <Tab value="calendar">
                        <div className='flex items-center gap-2 font-thin'>
                            <CalendarIcon className='w-4 h-4' /> Calendar
                        </div>
                    </Tab>
                    <Tab value="list">
                        <div className='flex items-center gap-2 font-thin'>
                            <ListIcon className='w-4 h-4' /> List
                        </div>
                    </Tab>
                </TabsHeader>
                <TabsBody>
                    <TabPanel value="calendar">
                        <div>
                            <Calendar
                                localizer={localizer}
                                events={events}
                                eventPropGetter={(event) => {
                                    return {
                                        className: 'text-xs font-bold',
                                        style: {
                                            backgroundColor: event.color || 'rgb(228, 148, 0)',
                                            border: '0px solid rgb(228, 148, 0)',
                                        }
                                    }
                                }}
                                selectable={view === 'day' || view === 'week' || view === 'month'}
                                onSelectSlot={handleOnTimeSlotSelect}
                                longPressThreshold={10}
                                startAccessor="start"
                                endAccessor="end"
                                style={{ height: 600 }}
                                view={view}
                                onView={setView}
                                resources={meetingRooms.map((meetingRoom) => ({
                                    id: meetingRoom._id,
                                    title: meetingRoom.code
                                }))}
                            />
                            <div className="flex justify-center items-center gap-2 mt-4">
                                {
                                    meetingRooms.map((meetingRoom, index) => (
                                        <div key={index} className='flex items-center gap-2'>
                                            <div className='h-4 w-4 rounded-full' style={{ backgroundColor: meetingRoom.idColor }}>

                                            </div>
                                            <div className='text-xs font-thin'>
                                                {meetingRoom.number} ({meetingRoom.code})
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                            <div className='flex flex-col gap-2 mt-4'>
                                <span className='text-lg font-thin'>
                                    Meeting Room Bookings Quota (Monthly)
                                </span>
                                <div>
                                    <Progress
                                        value={remainingMeetingRoomQuota}
                                        color={remainingMeetingRoomQuota > 50 ? "green" : remainingMeetingRoomQuota > 25 ? "yellow" : "red"}
                                        label={`Remaining`}
                                        className='w-full'
                                    />
                                </div>
                                <div className='flex gap-2 text-xs font-thin'>
                                    <div>
                                        Total: {userDetails.meetingRoomQuota.monthlyHours / 60} hours
                                    </div>
                                    <div>
                                        Used: {((userDetails.meetingRoomQuota.usedHours) / 60).toFixed(1)} hours
                                    </div>
                                    <div>
                                        Remaining: {((userDetails.meetingRoomQuota.monthlyHours - userDetails.meetingRoomQuota.usedHours) / 60).toFixed(1)} hours
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TabPanel>
                    <TabPanel value="list">
                        {
                            loading ?
                                <div className="flex justify-center">
                                    <Spinner />
                                </div> : (
                                    meetingRoomBookings.length === 0 ?
                                        <div className="flex justify-center mt-4">
                                            <Alert color="brown" variant="outlined">
                                                Click on the Date to book a Meeting Room.
                                            </Alert>
                                        </div> :
                                        <div className="overflow-x-auto overflow-y-auto">
                                            <table className="w-full min-w-max table-auto text-left">
                                                <thead className="text-sm">
                                                    <tr className="py-3">
                                                        <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Bookings</th>
                                                    </tr>
                                                    <tr className="py-3 uppercase">
                                                        <th className="py-3">Meeting Room</th>
                                                        <th className="py-3">Booking Time</th>
                                                        <th className="py-3">Booking Duration</th>
                                                        <th className="py-3">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {meetingRoomBookings.map((meetingRoomBooking, index) => (
                                                        <tr key={index} draggable className="border-t hover:bg-gray-50 button rounded-full">
                                                            <td className="font-thin py-3 text-sm">
                                                                <div className={`text-sm font-bold flex gap-2 items-center`}>
                                                                    <div className='w-4 h-4 rounded-full' style={{ backgroundColor: meetingRoomBooking.meetingRoom.idColor }}></div>
                                                                    <span className='text-sm font-bold'>{meetingRoomBooking.meetingRoom.code}</span>
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {meetingRoomBooking.meetingRoom.floor.building.name} - {meetingRoomBooking.meetingRoom.floor.building.branch.name}
                                                                </div>
                                                            </td>
                                                            <td className="font-thin py-3 text-sm">
                                                                <div className='text-sm font-bold'>
                                                                    {moment(meetingRoomBooking.startDate).format('LT')} - {moment(meetingRoomBooking.endDate).format('LT')}
                                                                </div>
                                                                <div className='text-xs text-gray-700'>
                                                                    {moment(meetingRoomBooking.startDate).format('ll')}
                                                                </div>
                                                            </td>
                                                            <td className="font-thin py-3 text-sm">
                                                                {minutesToText(meetingRoomBooking.slot)}
                                                            </td>
                                                            <td className="font-thin py-3 text-sm">
                                                                {
                                                                    meetingRoomBooking.user._id === userDetails._id ? (
                                                                        meetingRoomBooking.status !== VERIFICATION_TYPES.CANCELLED ?
                                                                            <IconButton size='sm' color='red' className='rounded-full' onClick={() => handleCancelBooking(meetingRoomBooking._id)}>
                                                                                <TrashIcon className='w-4 h-4' />
                                                                            </IconButton> :
                                                                            <div className='flex items-center gap-2'>
                                                                                <Chip color='yellow' value='Cancelled' />
                                                                            </div>
                                                                    ) : <></>
                                                                }
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>

                                            </table>
                                            <BottomUXSpace size={32} />
                                        </div>

                                )
                        }
                    </TabPanel>
                </TabsBody>
            </Tabs>



            <ModalX
                isOpen={modalOpenForBooking}
                onClose={() => setModalOpenForBooking(false)}
                title={
                    <div className='flex items-center justify-between gap-2 w-full'>
                        <div className='text-lg font-thin'>Book Meeting Room</div>
                        <div className='text-md font-thin'>
                            {moment(bookingFormData.startDate).format('h:mm A')} - {
                                bookingFormData.slot ?
                                    moment(bookingFormData.startDate).add(bookingFormData.slot, 'minutes').format('h:mm A') :
                                    <span>Slot <span className="text-red-500">*</span></span>

                            }
                            <div className='text-xs text-orange-900'>
                                {moment(bookingFormData.startDate).format('LL')}
                            </div>
                        </div>
                    </div>
                } maxWidth="xl">
                <form onSubmit={handleBookingFormSubmit}>
                    <div className="space-y-2">
                        <div>
                            <Select
                                label={<span>Select Meeting Room <span className="text-red-500">*</span></span>}
                                value={bookingFormData.meetingRoom}
                                onChange={newValue => setBookingFormData({ ...bookingFormData, meetingRoom: newValue })}
                            >
                                {
                                    meetingRooms.map((meetingRoom, index) => (
                                        <Option key={index} value={meetingRoom._id}>
                                            <div className='flex justify-between gap-2 items-center'>
                                                <div className='flex items-center gap-2'>
                                                    <div className='w-4 h-4 rounded-full' style={{ backgroundColor: meetingRoom.idColor }}></div>
                                                    <span className='text-sm font-bold'>{meetingRoom.code}</span>
                                                </div>
                                                <div className='text-xs text-gray-500'>
                                                    {meetingRoom.floor.building.name} - {meetingRoom.floor.building.branch.name}
                                                </div>
                                            </div>
                                        </Option>
                                    ))
                                }
                            </Select>
                        </div>
                        <div>
                            <Input
                                label="Title"
                                name="title"
                                required
                                value={bookingFormData.title}
                                onChange={(e) => setBookingFormData({ ...bookingFormData, title: e.target.value })}
                            />
                        </div>
                        <div>
                            <Textarea
                                label="Description"
                                name="description"
                                value={bookingFormData.description}
                                onChange={(e) => setBookingFormData({ ...bookingFormData, description: e.target.value })}
                            />
                        </div>
                        <div>
                            <Select
                                label={<span>Slot <span className="text-red-500">*</span></span>}
                                value={bookingFormData.slot}
                                onChange={newValue => setBookingFormData({ ...bookingFormData, slot: newValue })}
                            >
                                <Option value={'30'}>30 Minutes</Option>
                                <Option value={'60'}>1 Hour</Option>
                                <Option value={'90'}>1 Hour, 30 Minutes</Option>
                                <Option value={'120'}>2 Hours</Option>
                            </Select>
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-2">
                        <Button
                            onClick={() => setModalOpenForBooking(false)}
                            className="rounded-full"
                            disabled={loadingMutation}
                            variant="text"
                        >
                            Cancel
                        </Button>
                        <Button type="submit" className="rounded-full" disabled={loadingMutation} loading={loadingMutation}>
                            Book
                        </Button>
                    </div>
                </form>
            </ModalX>
            <ConfirmationDialog />
        </div>
    )
}

export default CustomerMeetingRoom;